import * as React from 'react'
import DialogTitle from '@mui/material/DialogTitle'
import Button from '@mui/material/Button'

import Grid from '@mui/material/Grid'

import DialogContent from '@mui/material/DialogContent'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import Dialog from '@mui/material/Dialog'
import LoadingButton from '@mui/lab/LoadingButton'

import { renderImportOptionsMenu } from './SubImportMenuItem'

import Stepper from '@mui/material/Stepper'
import Step from '@mui/material/Step'
import StepLabel from '@mui/material/StepLabel'
import StepButton from '@mui/material/StepButton'

import Typography from '@mui/material/Typography'

import ArrowForwardIcon from '@mui/icons-material/ArrowForwardOutlined'
import EditableSelect from '../EditableSelect'
import TextField from '@mui/material/TextField'

import {
    useContextsStore,
    selectOwnedContexts,
} from '../../stores/useContextsStore'
import { UserContext } from '../../UserContext'

import { useTheme } from '@mui/material/styles'

import { SubImportContext } from './SubImportContext'
import { DialogsContext } from '../../DialogsContext'

import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import FormHelperText from '@mui/material/FormHelperText'
import FormControlLabel from '@mui/material/FormControlLabel'

import Switch from '@mui/material/Switch'
import Chip from '@mui/material/Chip'
import Box from '@mui/material/Box'

import axios from 'axios'
import qs from 'qs'

import { slugifyString } from '../../utils/index'
import { generateTextImportItems } from './TextImportItems'
import ImportingNotice from './ImportingNotice'
import { chipHoverAccent } from '../../theme'
import { importErrorMessage } from './importError'

const steps = ['Start', 'Sources', 'Settings', 'Results']

export default function YouTubeKeywordsImportDialog({
    presetContext = '',
    presetSearchString = '',
    open,
    onClose,
    type,
}) {
    const theme = useTheme() // hook to get the current theme

    const {
        activeSubImportDialog,
        setActiveSubImportDialog,
    } = React.useContext(SubImportContext)

    const {
        activeImportDialog,
        setActiveImportDialog,
        backLinkDialog,
        openImportDialog,
        closeImportDialog,
        setPresetSearchString,
    } = React.useContext(DialogsContext)

    // Shared across all import sources so the typed query persists when switching
    const searchString = presetSearchString
    const setSearchString = setPresetSearchString

    const [selectedContext, setSelectedContext] = React.useState(presetContext)

    const [errorMessage, setErrorMessage] = React.useState(null)

    const [importError, setImportError] = React.useState(null)

    const [completedSteps, setCompletedSteps] = React.useState([])

    const [visitedContext, setVisitedContext] = React.useState(false)

    const [numberOfResults, setNumberOfResults] = React.useState('50')

    const [searchLanguage, setSearchLanguage] = React.useState('Default')

    const [searchCountry, setSearchCountry] = React.useState('US')

    const [hideSearchTerms, setHideSearchTerms] = React.useState(true)

    const [excludePageTitles, setExcludePageTitles] = React.useState(false)

    const [modifyAnalyzedText, setModifyAnalyzedText] = React.useState('none')

    const [replaceEntities, setReplaceEntities] = React.useState(false)

    const [includeAuthors, setIncludeAuthors] = React.useState(false)

    const [sortBy, setSortBy] = React.useState('relevance')

    const [
        excludeVideoDescriptions,
        setExcludeVideoDescriptions,
    ] = React.useState(false)

    const [contentToAnalyze, setContentToAnalyze] = React.useState(
        'search_results'
    )

    React.useEffect(() => {
        if (contentToAnalyze == 'videos') {
            setNumberOfResults('10')
        }
        if (contentToAnalyze == 'search_results') {
            setNumberOfResults('50')
        }
    }, [contentToAnalyze])

    const [advancedSettings, setAdvancedSettings] = React.useState(false)

    const menuItems = generateTextImportItems()

    const filterForMenus =
        type == 'youtubeVideos' ? 'youtubeVideos' : 'youtubeKeywords'
    const filteredMenuItems = menuItems.filter(
        ({ textInput }) =>
            textInput.includes(filterForMenus) ||
            textInput.includes(backLinkDialog)
    )

    const { username } = React.useContext(UserContext)
    const contexts = useContextsStore(selectOwnedContexts)
    const contextNames = contexts.map(c => c.name)

    const [activeStep, setActiveStep] = React.useState(2)
    const [skipped, setSkipped] = React.useState(new Set())

    const handleNext = () => {
        let newSkipped = skipped

        setActiveStep(prevActiveStep => prevActiveStep + 1)
        setSkipped(newSkipped)
    }

    const handleBack = () => {
        setActiveStep(prevActiveStep => prevActiveStep - 1)
    }

    const handleStep = step => {
        setActiveStep(step)
    }

    const handleNumberOfResultsChange = event => {
        setNumberOfResults(event.target.value)
    }

    const handleSearchStringChange = event => {
        setSearchString(event.target.value)
    }

    const handleSearchLanguageChange = event => {
        setSearchLanguage(event.target.value)
    }

    const handleSearchCountryChange = event => {
        setSearchCountry(event.target.value)
    }

    React.useEffect(() => {
        if (activeSubImportDialog.type) {
            setActiveStep(2)
            setActiveImportDialog(activeSubImportDialog.type)
        }
    }, [activeSubImportDialog])

    React.useEffect(() => {
        switch (activeStep) {
            case 0: // Start
                setActiveSubImportDialog(false)
                setActiveImportDialog('import')
                break
            case 1: // Sources
                setActiveSubImportDialog(false)
                setActiveImportDialog('shortTextImport')
                break
            case 2: // Settings
                if (!presetContext) {
                    setSelectedContext(generatePresetContext(searchString))
                }
                break
            case 3: // Analysis
                if (!searchString) {
                    setErrorMessage(
                        `Please, provide a search query you'd like to get results from Google for.`
                    )
                    setActiveStep(2)
                    break
                }
                if (!selectedContext) {
                    setErrorMessage(
                        'Please, select the graph to import your data to.'
                    )
                    setActiveStep(2)
                    break
                }
                if (selectedContext) {
                    setCompletedSteps([...completedSteps, 3])
                    setErrorMessage(null)
                    submitDataForm()
                    break
                }
        }
    }, [activeStep])

    const submitDataForm = () => {
        const dataToSend = {
            searchQuery: searchString,
            limit: numberOfResults,
            importLanguage: searchLanguage === 'Default' ? '' : searchLanguage,
            importRegion: searchCountry === 'US' ? '' : searchCountry,
            excludesearchquery: hideSearchTerms ? '1' : '',
            excludeDescriptions: excludeVideoDescriptions ? '1' : '',
            searchMode:
                contentToAnalyze == 'search_results'
                    ? 'search'
                    : 'searchVideos',
            settings: 'morphemes',
            context: selectedContext,
            modifyAnalyzedText: modifyAnalyzedText,
            replaceEntities: replaceEntities,
            includeAuthors: includeAuthors,
            sortBy: sortBy,
        }

        const dataAsForm = qs.stringify(dataToSend)

        axios
            .post('/import/youtube', dataAsForm, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded', // Sending as URL-encoded form data
                },
            })
            .then(response => {
                if (response.data.redirectUrl) {
                    // If you're using window.location
                    window.location.href = response.data.redirectUrl

                    // Or, if you're using React Router's history (assuming you have access to the history object)
                    // history.push(response.data.redirectUrl);
                } else {
                    console.error(`Did't receive any response from the server.`)
                    if (response.data.error) {
                        setErrorMessage(
                            typeof response.data.error == 'object'
                                ? response.data.error.error ||
                                      JSON.stringify(response.data.error)
                                : response.data.error
                        )
                        setImportError(response.data.error)
                        setActiveStep(2)
                    }
                }
            })
            .catch(error => {
                // Handle the error here
                console.error('Received the following error', error)
                setErrorMessage(importErrorMessage(error))
                setImportError(response.data.error)
            })
    }

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                fullWidth={true}
                maxWidth="lg"
            >
                <DialogTitle
                    sx={{
                        padding: 4,
                        color: theme => theme.palette.text.primary,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    {type == 'youtubeVideos'
                        ? 'YouTube Content Analysis'
                        : 'YouTube Keyword Research'}
                    <IconButton
                        edge="end"
                        color="inherit"
                        onClick={onClose}
                        aria-label="close"
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <Stepper
                    activeStep={activeStep}
                    sx={{ paddingLeft: 4, paddingRight: 4, paddingTop: 2 }}
                >
                    {steps.map((label, index) => {
                        const stepProps = {}
                        const labelProps = {}

                        return (
                            <Step key={label} {...stepProps}>
                                <StepButton
                                    onClick={() => handleStep(index)}
                                    completed={(
                                        completedSteps.indexOf(index) >= 0
                                    ).toString()}
                                >
                                    <StepLabel
                                        {...labelProps}
                                        sx={{
                                            '& .MuiStepLabel-label': {
                                                fontSize: {
                                                    xs: '0.75rem',
                                                    sm: 'initial',
                                                },
                                            },
                                        }}
                                    >
                                        {label}
                                    </StepLabel>
                                </StepButton>
                            </Step>
                        )
                    })}
                </Stepper>

                {errorMessage && (
                    <Typography
                        sx={{
                            paddingRight: 4,
                            paddingLeft: 4,
                            paddingTop: 4,
                            paddingBottom: 0,
                        }}
                        color="error"
                        component="div"
                        gutterBottom
                    >
                        {errorMessage}
                    </Typography>
                )}

                {activeStep === 2 && (
                    <>
                        <DialogContent
                            sx={{
                                paddingRight: 4,
                                paddingLeft: 4,
                                paddingTop: 4,
                            }}
                        >
                            <DialogContentText>
                                Visualize YouTube content for a topic or search
                                query:
                            </DialogContentText>

                            <Grid container spacing={8}>
                                <Grid item xs={12} sm={7}>
                                    <FormControl
                                        fullWidth
                                        sx={{ marginTop: 4 }}
                                    >
                                        <TextField
                                            label={'YouTube search query'}
                                            value={searchString}
                                            variant="outlined"
                                            onChange={handleSearchStringChange}
                                            aria-describedby="searchString-helperText"
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        borderWidth: 2,
                                                        borderColor:
                                                            'info.main',
                                                    },
                                                    '&:hover fieldset': {
                                                        borderWidth: 2,
                                                        borderColor:
                                                            'info.dark',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderWidth: 2,
                                                    },
                                                },
                                            }}
                                        />
                                        {advancedSettings && (
                                            <FormHelperText
                                                id="searchString-helperText"
                                                sx={{ marginTop: 2 }}
                                            >
                                                You can use it to find the
                                                videos you like and to better
                                                understand how to market your
                                                own content.
                                            </FormHelperText>
                                        )}
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                alignItems: 'center',
                                                gap: 1.5,
                                                mt: 2.5,
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ fontWeight: 500 }}
                                            >
                                                try one of these:
                                            </Typography>
                                            {[
                                                'ai tools',
                                                'chatgpt',
                                                'claude',
                                                'politics',
                                            ].map(suggestion => (
                                                <Chip
                                                    key={suggestion}
                                                    label={suggestion}
                                                    size="medium"
                                                    variant="outlined"
                                                    onClick={() =>
                                                        setSearchString(
                                                            suggestion
                                                        )
                                                    }
                                                    sx={{
                                                        cursor: 'pointer',
                                                        fontSize: '0.85rem',
                                                        fontWeight: 500,
                                                        borderColor:
                                                            'rgba(0, 200, 200, 0.5)',
                                                        color: 'info.main',
                                                        '&:hover': {
                                                            color: chipHoverAccent,
                                                            borderColor: chipHoverAccent,
                                                        },
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                    </FormControl>
                                    <FormControl
                                        fullWidth
                                        sx={{ marginTop: 6 }}
                                    >
                                        <DialogContentText>
                                            Using the import app:
                                        </DialogContentText>
                                        <DialogContent
                                            sx={{
                                                pl: 0,
                                                ml: 0,
                                                marginTop: -2,
                                            }}
                                        >
                                            {renderImportOptionsMenu(
                                                filteredMenuItems,
                                                filterForMenus
                                            )}
                                        </DialogContent>
                                    </FormControl>
                                </Grid>
                                <Grid
                                    item
                                    xs={12}
                                    sm={4}
                                    sx={{
                                        marginTop: 0,
                                        marginBottom: 4,
                                    }}
                                >
                                    <FormControl
                                        fullWidth
                                        sx={{ marginTop: 4, marginBottom: 2 }}
                                    >
                                        <EditableSelect
                                            selectItems={contextNames}
                                            label={'Graph name'}
                                            initialValue={selectedContext}
                                            onChange={name =>
                                                setSelectedContext(name)
                                            }
                                        />
                                    </FormControl>

                                    <FormControl
                                        fullWidth
                                        sx={{
                                            marginTop: 4,
                                            marginBottom: 2,
                                        }}
                                    >
                                        <InputLabel id="contentToAnalyze-label">
                                            Content to analyze
                                        </InputLabel>
                                        <Select
                                            labelId="contentToAnalyze-label"
                                            id="contentToAnalyze"
                                            value={contentToAnalyze}
                                            onChange={event =>
                                                setContentToAnalyze(
                                                    event.target.value
                                                )
                                            }
                                            label="Content to analyze"
                                        >
                                            <MenuItem
                                                value={'search_results'}
                                                key="1"
                                            >
                                                video titles and descriptions
                                            </MenuItem>
                                            <MenuItem value={'videos'} key="2">
                                                video content (subtitles)
                                            </MenuItem>
                                        </Select>
                                        {/* {contentToAnalyze ==
                                            'search_results' && (
                                            <FormHelperText
                                                sx={{ marginTop: 1 }}
                                            >
                                                Analyze the titles and
                                                descriptions of the videos
                                                provided for your query.
                                            </FormHelperText>
                                        )}
                                        {contentToAnalyze == 'videos' && (
                                            <FormHelperText
                                                sx={{ marginTop: 1 }}
                                            >
                                                Analyze the subtitles of the
                                                videos provided by YouTube for
                                                your search query.
                                            </FormHelperText>
                                        )} */}
                                    </FormControl>

                                    <FormControl
                                        fullWidth
                                        sx={{
                                            marginTop: 2,
                                            marginBottom: 2,
                                        }}
                                    >
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={advancedSettings}
                                                    onChange={event =>
                                                        setAdvancedSettings(
                                                            event.target.checked
                                                        )
                                                    }
                                                />
                                            }
                                            label="Show advanced settings"
                                        />
                                    </FormControl>

                                    {advancedSettings && (
                                        <>
                                            <FormControl
                                                fullWidth
                                                sx={{
                                                    marginTop: 4,
                                                    marginBottom: 2,
                                                }}
                                            >
                                                <InputLabel id="sortBy-label">
                                                    Sort videos by
                                                </InputLabel>
                                                <Select
                                                    labelId="sortBy-label"
                                                    id="sortBy"
                                                    value={sortBy}
                                                    onChange={event =>
                                                        setSortBy(
                                                            event.target.value
                                                        )
                                                    }
                                                    label="Sort videos by"
                                                >
                                                    <MenuItem
                                                        value={'relevance'}
                                                        key="1"
                                                    >
                                                        relevance (youtube's
                                                        default)
                                                    </MenuItem>
                                                    <MenuItem
                                                        value={'viewcount'}
                                                        key="2"
                                                    >
                                                        video view count
                                                    </MenuItem>
                                                    <MenuItem
                                                        value={'date'}
                                                        key="3"
                                                    >
                                                        publication date
                                                    </MenuItem>
                                                    <MenuItem
                                                        value={'rating'}
                                                        key="3"
                                                    >
                                                        video rating
                                                    </MenuItem>
                                                </Select>
                                            </FormControl>

                                            <FormControl
                                                fullWidth
                                                sx={{
                                                    marginTop: 4,
                                                    marginBottom: 2,
                                                }}
                                            >
                                                <InputLabel id="numberOfResults-label">
                                                    Number of results to analyze
                                                </InputLabel>

                                                {contentToAnalyze ==
                                                    'videos' && (
                                                    <Select
                                                        labelId="numberOfResults-label"
                                                        id="numberOfResults"
                                                        value={numberOfResults}
                                                        onChange={
                                                            handleNumberOfResultsChange
                                                        }
                                                        label="Number of Results to Analyze"
                                                    >
                                                        <MenuItem value={'10'}>
                                                            top 10 results
                                                        </MenuItem>
                                                        <MenuItem value={'20'}>
                                                            top 20 results
                                                        </MenuItem>
                                                        <MenuItem value={'30'}>
                                                            top 30 results
                                                        </MenuItem>
                                                        <MenuItem value={'40'}>
                                                            top 40 results
                                                        </MenuItem>
                                                        <MenuItem value={'50'}>
                                                            top 50 results
                                                        </MenuItem>
                                                        <MenuItem value={'100'}>
                                                            top 100 results
                                                        </MenuItem>
                                                    </Select>
                                                )}
                                                {contentToAnalyze ==
                                                    'search_results' && (
                                                    <Select
                                                        labelId="numberOfResults-label"
                                                        id="numberOfResults"
                                                        value={numberOfResults}
                                                        onChange={
                                                            handleNumberOfResultsChange
                                                        }
                                                        label="Number of Results to Analyze"
                                                    >
                                                        <MenuItem value={'10'}>
                                                            top 10 results
                                                        </MenuItem>
                                                        <MenuItem value={'20'}>
                                                            top 20 results
                                                        </MenuItem>
                                                        <MenuItem value={'30'}>
                                                            top 30 results
                                                        </MenuItem>
                                                        <MenuItem value={'40'}>
                                                            top 40 results
                                                        </MenuItem>
                                                        <MenuItem value={'50'}>
                                                            top 50 results
                                                        </MenuItem>
                                                        <MenuItem value={'100'}>
                                                            top 100 results
                                                        </MenuItem>
                                                        <MenuItem value={'200'}>
                                                            top 200 results
                                                        </MenuItem>
                                                        <MenuItem value={'500'}>
                                                            top 500 results
                                                        </MenuItem>
                                                        <MenuItem
                                                            value={'1000'}
                                                        >
                                                            top 1000 results
                                                        </MenuItem>
                                                        <MenuItem
                                                            value={'2000'}
                                                        >
                                                            top 2000 results
                                                        </MenuItem>
                                                    </Select>
                                                )}
                                            </FormControl>
                                            <FormControl
                                                fullWidth
                                                sx={{
                                                    marginTop: 4,
                                                    marginBottom: 2,
                                                }}
                                            >
                                                <InputLabel id="searchLanguage-label">
                                                    Language of the search
                                                    results
                                                </InputLabel>
                                                <Select
                                                    labelId="searchLanguage-label"
                                                    id="searchLanguage"
                                                    value={searchLanguage}
                                                    onChange={
                                                        handleSearchLanguageChange
                                                    }
                                                    label="Language of the Search Results"
                                                >
                                                    <MenuItem value={'Default'}>
                                                        Default Language
                                                    </MenuItem>
                                                    <MenuItem value={'EN'}>
                                                        English
                                                    </MenuItem>
                                                    <MenuItem value={'CN'}>
                                                        Chinese (Simplified)
                                                    </MenuItem>
                                                    <MenuItem value={'TW'}>
                                                        Chinese (Traditional)
                                                    </MenuItem>
                                                    <MenuItem value={'NL'}>
                                                        Dutch
                                                    </MenuItem>
                                                    <MenuItem value={'FR'}>
                                                        French
                                                    </MenuItem>
                                                    <MenuItem value={'DE'}>
                                                        German
                                                    </MenuItem>
                                                    <MenuItem value={'IT'}>
                                                        Italian
                                                    </MenuItem>
                                                    <MenuItem value={'JP'}>
                                                        Japanese
                                                    </MenuItem>
                                                    <MenuItem value={'NO'}>
                                                        Norwegian
                                                    </MenuItem>
                                                    <MenuItem value={'PT'}>
                                                        Portugese
                                                    </MenuItem>
                                                    <MenuItem value={'RU'}>
                                                        Russian
                                                    </MenuItem>
                                                    <MenuItem value={'ES'}>
                                                        Spanish
                                                    </MenuItem>
                                                    <MenuItem value={'SV'}>
                                                        Swedish
                                                    </MenuItem>
                                                </Select>
                                            </FormControl>
                                            <FormControl
                                                fullWidth
                                                sx={{
                                                    marginTop: 4,
                                                    marginBottom: 2,
                                                }}
                                            >
                                                <InputLabel id="searchCountry-label">
                                                    For users located in
                                                </InputLabel>
                                                <Select
                                                    labelId="searchCountry-label"
                                                    id="searchCountry"
                                                    value={searchCountry}
                                                    onChange={
                                                        handleSearchCountryChange
                                                    }
                                                    label="For Users Located In"
                                                >
                                                    <MenuItem value={'US'}>
                                                        USA
                                                    </MenuItem>
                                                    <MenuItem value={'AR'}>
                                                        Argentina
                                                    </MenuItem>
                                                    <MenuItem value={'AU'}>
                                                        Australia
                                                    </MenuItem>
                                                    <MenuItem value={'BR'}>
                                                        Brazil
                                                    </MenuItem>
                                                    <MenuItem value={'CA'}>
                                                        Canada
                                                    </MenuItem>
                                                    <MenuItem value={'CN'}>
                                                        China
                                                    </MenuItem>
                                                    <MenuItem value={'FR'}>
                                                        France
                                                    </MenuItem>
                                                    <MenuItem value={'DE'}>
                                                        Germany
                                                    </MenuItem>
                                                    <MenuItem value={'GB'}>
                                                        Great Britain
                                                    </MenuItem>
                                                    <MenuItem value={'HK'}>
                                                        Hong-Kong
                                                    </MenuItem>
                                                    <MenuItem value={'IN'}>
                                                        India
                                                    </MenuItem>
                                                    <MenuItem value={'IT'}>
                                                        Italy
                                                    </MenuItem>
                                                    <MenuItem value={'JP'}>
                                                        Japan
                                                    </MenuItem>
                                                    <MenuItem value={'MX'}>
                                                        Mexico
                                                    </MenuItem>
                                                    <MenuItem value={'NL'}>
                                                        Netherlands
                                                    </MenuItem>
                                                    <MenuItem value={'NZ'}>
                                                        New Zealand
                                                    </MenuItem>
                                                    <MenuItem value={'NO'}>
                                                        Norway
                                                    </MenuItem>
                                                    <MenuItem value={'PT'}>
                                                        Portugal
                                                    </MenuItem>
                                                    <MenuItem value={'RU'}>
                                                        Russia
                                                    </MenuItem>
                                                    <MenuItem value={'ES'}>
                                                        Spain
                                                    </MenuItem>
                                                    <MenuItem value={'SV'}>
                                                        Sweden
                                                    </MenuItem>
                                                    <MenuItem value={'TW'}>
                                                        Taiwan
                                                    </MenuItem>
                                                    <MenuItem value={'CH'}>
                                                        Switzerland
                                                    </MenuItem>
                                                    <MenuItem value={'GB'}>
                                                        United Kingdom
                                                    </MenuItem>
                                                </Select>
                                            </FormControl>
                                            <FormControl
                                                fullWidth
                                                sx={{
                                                    marginTop: 4,
                                                    marginBottom: 2,
                                                }}
                                            >
                                                <InputLabel id="modifyAnalyzedText-label">
                                                    Build a knowledge graph from
                                                </InputLabel>
                                                <Select
                                                    labelId="modifyAnalyzedText-label"
                                                    id="modifyAnalyzedText"
                                                    value={modifyAnalyzedText}
                                                    onChange={event =>
                                                        setModifyAnalyzedText(
                                                            event.target.value
                                                        )
                                                    }
                                                    label="Build a knowledge graph from"
                                                >
                                                    <MenuItem
                                                        value={'none'}
                                                        key="1"
                                                    >
                                                        the words used in text
                                                    </MenuItem>
                                                    <MenuItem
                                                        value={'detectEntities'}
                                                        key="2"
                                                    >
                                                        the words and detected
                                                        entities
                                                    </MenuItem>
                                                    <MenuItem
                                                        value={
                                                            'extractEntitiesOnly'
                                                        }
                                                        key="3"
                                                    >
                                                        only the detected
                                                        entities
                                                    </MenuItem>
                                                </Select>
                                                {modifyAnalyzedText !=
                                                    'none' && (
                                                    <FormHelperText
                                                        sx={{ marginTop: 1 }}
                                                    >
                                                        By default, we extract
                                                        the words used in your
                                                        text. You can also
                                                        choose to detect and tag
                                                        entities in your text
                                                        and build a graph from
                                                        them.
                                                    </FormHelperText>
                                                )}
                                            </FormControl>
                                            {modifyAnalyzedText != 'none' && (
                                                <FormControl
                                                    fullWidth
                                                    sx={{
                                                        marginTop: 2,
                                                        marginBottom: 2,
                                                    }}
                                                >
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                id="replaceEntities"
                                                                checked={
                                                                    replaceEntities
                                                                }
                                                                onChange={event =>
                                                                    setReplaceEntities(
                                                                        event
                                                                            .target
                                                                            .checked
                                                                    )
                                                                }
                                                            />
                                                        }
                                                        label="Replace entities with root concepts"
                                                    />
                                                    {replaceEntities && (
                                                        <FormHelperText
                                                            id="replaceEntities-helperText"
                                                            sx={{
                                                                marginTop: 1,
                                                            }}
                                                        >
                                                            When turned on, we
                                                            will not only detect
                                                            the entities, but
                                                            also replace them
                                                            with their
                                                            ontological root
                                                            concepts throughout
                                                            your text.
                                                        </FormHelperText>
                                                    )}
                                                </FormControl>
                                            )}
                                            <FormControl
                                                fullWidth
                                                sx={{
                                                    marginTop: 4,
                                                    marginBottom: 2,
                                                }}
                                            >
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={
                                                                hideSearchTerms
                                                            }
                                                            onChange={event =>
                                                                setHideSearchTerms(
                                                                    event.target
                                                                        .checked
                                                                )
                                                            }
                                                        />
                                                    }
                                                    label="Hide search terms from graph"
                                                />

                                                <FormHelperText>
                                                    explore the context around
                                                    the search query
                                                </FormHelperText>
                                            </FormControl>

                                            <FormControl
                                                fullWidth
                                                sx={{
                                                    marginTop: 2,
                                                    marginBottom: 2,
                                                }}
                                            >
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={
                                                                excludeVideoDescriptions
                                                            }
                                                            onChange={event =>
                                                                setExcludeVideoDescriptions(
                                                                    event.target
                                                                        .checked
                                                                )
                                                            }
                                                        />
                                                    }
                                                    label="Exclude video descriptions"
                                                />

                                                <FormHelperText>
                                                    analyze only the titles of
                                                    the videos
                                                </FormHelperText>
                                            </FormControl>

                                            <FormControl
                                                fullWidth
                                                sx={{
                                                    marginTop: 4,
                                                    marginBottom: 2,
                                                }}
                                            >
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={
                                                                includeAuthors
                                                            }
                                                            onChange={event =>
                                                                setIncludeAuthors(
                                                                    event.target
                                                                        .checked
                                                                )
                                                            }
                                                        />
                                                    }
                                                    label="include the video authors' usernames"
                                                />

                                                <FormHelperText>
                                                    will show a social layer on
                                                    top with usernames as
                                                    @mentions
                                                </FormHelperText>
                                            </FormControl>
                                        </>
                                    )}
                                </Grid>
                            </Grid>
                        </DialogContent>
                    </>
                )}

                {activeStep === 3 && (
                    <ImportingNotice
                        linkHref={`/${username}/${selectedContext}/edit`}
                    />
                )}
                <DialogActions
                    sx={{
                        paddingRight: 4,
                        paddingLeft: 4,
                        paddingBottom: 4,
                        paddingTop: 2,
                    }}
                >
                    {/* <Button onClick={onClose} variant="outlined">
                        Cancel
                    </Button> */}
                    <Button
                        color="inherit"
                        variant="outlined"
                        disabled={activeStep === 0}
                        onClick={handleBack}
                        sx={{ mr: 1, px: 3, py: 1, fontSize: '0.95rem' }}
                    >
                        Back
                    </Button>
                    {/* {isStepOptional(activeStep) && (
                        <Button
                            color="inherit"
                            onClick={handleSkip}
                            sx={{ mr: 1 }}
                        >
                            Skip
                        </Button>
                    )} */}
                    <LoadingButton
                        color="info"
                        variant="contained"
                        size="large"
                        endIcon={<ArrowForwardIcon />}
                        disabled={!searchString.length > 0 || activeStep == 3}
                        onClick={handleNext}
                        sx={{ px: 4, py: 1, fontSize: '0.95rem' }}
                    >
                        {activeStep === 2 ? 'Visualize' : 'Next'}
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </>
    )
}

function generatePresetContext(searchString) {
    const dateAndTime = new Date()
        .toISOString()
        .slice(2, 16)
        .replace(/-/g, '')
        .replace(/:/g, '')

    if (!searchString) return `youtube_${dateAndTime}`

    const twoFirstSearchQueryWords = slugifyString(
        searchString
            .split(' ')
            .slice(0, 2)
            .join('_')
            .slice(0, 20)
            .replace(/[^\P{L}a-z][^a-z]*/giu, '')
    )

    return `youtube_${twoFirstSearchQueryWords}`
}
